import { useEffect, useRef, useState } from 'react'
import { useSsoToken } from './useSsoToken'
import { ApiNotFoundError, postSimpleAttach, type SimpleAttachResponse } from '../services/claimsApi'
import { getSelectedMessageAsEml, getSimpleAttachAvailability } from '../utils/officeHelpers'
import { getApiErrorMessage } from '../utils/errorMessages'

/* global Office */

// getAsFileAsync hands back the EML as Base64; the upload needs the raw bytes.
function base64ToBlob(base64: string): Blob {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  // atob yields one char per byte (0–255), so codePointAt never meets a surrogate pair here.
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.codePointAt(i) ?? 0
  return new Blob([bytes], { type: 'message/rfc822' })
}

// Attach to MC1 (Simple Attach). Reads the open message as its original EML and uploads it to the
// backend, which appends the connected claim's number to the subject and places it in the MC1
// ingestion mailbox. The backend never reads the user's mailbox, so no mailbox read permission
// is involved.
//
// The pane is pinnable, so the selected email can change under it. Everything here describes the
// selected email, so on ItemChanged the availability is re-read and the previous result and error
// are dropped; an upload still in flight when the email changes is ignored when it lands.
export function useSimpleAttach(claimNumber: string) {
  const { getToken } = useSsoToken()
  const [availability, setAvailability] = useState(getSimpleAttachAvailability)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<SimpleAttachResponse | null>(null)
  // Bumped on every ItemChanged, so a send can tell whether the email changed while it ran.
  const itemVersion = useRef(0)

  useEffect(() => {
    const mailbox = Office?.context?.mailbox
    if (!mailbox?.addHandlerAsync) return

    try {
      mailbox.addHandlerAsync(Office.EventType.ItemChanged, () => {
        itemVersion.current++
        setAvailability(getSimpleAttachAvailability())
        setResult(null)
        setError(null)
      })
    } catch {
      // Event not supported in this Outlook version
      return
    }

    return () => {
      try {
        mailbox.removeHandlerAsync?.(Office.EventType.ItemChanged)
      } catch {
        // Nothing to remove
      }
    }
  }, [])

  const send = async (): Promise<boolean> => {
    const current = getSimpleAttachAvailability()
    if (!current.supported) {
      setError(current.reason ?? 'This email cannot be attached to MC1.')
      return false
    }

    const version = itemVersion.current
    setSending(true)
    setError(null)
    setResult(null)
    try {
      const [token, eml] = await Promise.all([getToken(), getSelectedMessageAsEml()])
      const response = await postSimpleAttach(claimNumber, base64ToBlob(eml), token)
      if (itemVersion.current === version) setResult(response)
      return true
    } catch (err: unknown) {
      console.error('[SimpleAttach] Failed to attach the email:', err)
      if (itemVersion.current === version) {
        setError(
          err instanceof ApiNotFoundError
            ? "You don't have access to this claim."
            : getApiErrorMessage(err, 'Failed to attach the email. Please try again.')
        )
      }
      return false
    } finally {
      setSending(false)
    }
  }

  return { availability, sending, error, result, send }
}
